import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

export type RecurrenceFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

export interface PerformanceEvent {
  id: number;
  performanceName: string;
  performerName: string;
  eventDate: string;
  weekDay: string;
  isRecurring: boolean;
  recurrenceFrequency: RecurrenceFrequency | null;
  recurrenceDayOfWeek: string | null;
  seriesId: string | null;
}

export interface CreatePerformanceEventRequest {
  performanceName: string;
  performerName: string;
  eventDate: string;
  isRecurring?: boolean;
  recurrenceFrequency?: RecurrenceFrequency | null;
  recurrenceDayOfWeek?: string | null;
}

export interface UpdatePerformanceEventRequest {
  performanceName?: string;
  performerName?: string;
  eventDate?: string;
  isRecurring?: boolean;
  recurrenceFrequency?: RecurrenceFrequency | null;
  recurrenceDayOfWeek?: string | null;
}

export interface PaginatedPerformanceEventsResponse {
  data: PerformanceEvent[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PerformanceEventPaginationParams {
  page?: number;
  limit?: number;
  type?: 'history';
}

export interface CreatePerformanceEventResult {
  event: PerformanceEvent;
  occurrencesCreated: number;
}

export const performanceEventsApi = {
  getPerformanceEvents: async (
    params: PerformanceEventPaginationParams = {},
    token: string
  ): Promise<PaginatedPerformanceEventsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.type) searchParams.append('type', params.type);

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.PERFORMANCE_EVENTS.BASE)}?${searchParams.toString()}`;

    return apiRequest<PaginatedPerformanceEventsResponse>(url, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
  },

  getPerformanceEvent: async (id: number, token: string): Promise<PerformanceEvent> => {
    return apiRequest<PerformanceEvent>(
      buildApiUrl(API_CONFIG.ENDPOINTS.PERFORMANCE_EVENTS.BY_ID(id)),
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  createPerformanceEvent: async (
    data: CreatePerformanceEventRequest,
    token: string
  ): Promise<CreatePerformanceEventResult> => {
    return apiRequest<CreatePerformanceEventResult>(
      buildApiUrl(API_CONFIG.ENDPOINTS.PERFORMANCE_EVENTS.BASE),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  updatePerformanceEvent: async (
    id: number,
    data: UpdatePerformanceEventRequest,
    token: string
  ): Promise<PerformanceEvent> => {
    return apiRequest<PerformanceEvent>(
      buildApiUrl(API_CONFIG.ENDPOINTS.PERFORMANCE_EVENTS.BY_ID(id)),
      {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  deletePerformanceEvent: async (id: number, token: string): Promise<void> => {
    return apiRequest<void>(
      buildApiUrl(API_CONFIG.ENDPOINTS.PERFORMANCE_EVENTS.BY_ID(id)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },

  deletePerformanceEventSeries: async (seriesId: string, token: string): Promise<{ deletedCount: number }> => {
    return apiRequest<{ deletedCount: number }>(
      buildApiUrl(API_CONFIG.ENDPOINTS.PERFORMANCE_EVENTS.BY_SERIES_ID(seriesId)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },
};
